import { type ElementType, type HTMLAttributes, type ReactNode, type Ref } from "react";
import styled, { keyframes, type RuleSet } from "styled-components";

import { xScrollbarStyles, xyScrollbarStyles, yScrollbarStyles } from "../foundations/scrollbar";

export type ScrollbarOrientation = "vertical" | "horizontal" | "both";

// gutter 예약 정책. `stable`은 스크롤바가 없어도 inline-end gutter를 항상 확보해 콘텐츠 폭이 흔들리지
// 않게 하고, `auto`는 실제 스크롤바가 있을 때만 확보해 빈 gutter 띠를 피한다.
export type ScrollbarGutter = "stable" | "auto";

// orientation별로 c6 foundations 스크롤 스타일 조각(thin themed thumb + overflow 방향)을 고른다.
const ORIENTATION_STYLES: Record<ScrollbarOrientation, RuleSet> = {
  vertical: yScrollbarStyles,
  horizontal: xScrollbarStyles,
  both: xyScrollbarStyles,
};

// 축별 overscroll 격리. scroll timeline에 묶여 그 축에 스크롤할 것이 있을 때만 적용된다.
const containBlockOverscroll = keyframes`
  from, to {
    overscroll-behavior-y: contain;
  }
`;
const containInlineOverscroll = keyframes`
  from, to {
    overscroll-behavior-x: contain;
  }
`;

const StyledScrollbar = styled.div<{
  $orientation: ScrollbarOrientation;
  $gutter: ScrollbarGutter;
}>`
  /* flex/grid 자식이 부모를 뚫고 넘치지 않도록 min-size:auto 기본값을 무력화. */
  min-width: 0;
  min-height: 0;
  /* 안쪽을 끝까지 스크롤해도 바깥(페이지)으로 스크롤이 번지지 않게 격리. */
  overscroll-behavior: contain;
  /* 격리는 스크롤할 것이 있는 축에만 건다. 조건 없이 걸면 넘치지 않는 판이 휠을 삼켜, 그 위에서는
     바깥도 스크롤되지 않는다. scroll timeline은 그 축에 넘침이 없으면 비활성이라 keyframe이 적용되지
     않는다. fill은 스크롤 양 끝(진행 0%·100%)에서도 격리가 남게 한다. timeline을 지원하지 않는
     브라우저는 위 선언(항상 격리)을 쓴다. 격리를 풀려면 소비처가 animation을 덮어쓴다(animation이
     일반 선언보다 우선한다). */
  @supports (animation-timeline: scroll()) {
    overscroll-behavior: auto;
    animation-name: ${containBlockOverscroll}, ${containInlineOverscroll};
    animation-timing-function: linear;
    animation-fill-mode: both;
    animation-timeline: scroll(self y), scroll(self x);
  }
  /* gutter 정책. 기본 stable은 스크롤바 등장 시 콘텐츠 폭 흔들림을 막지만, 테두리 프레임 안에서 세로
     스크롤바가 없을 때 빈 띠로 보이는 표면(데이터 그리드 등)은 auto로 실제 스크롤바가 있을 때만 확보한다. */
  scrollbar-gutter: ${({ $gutter }) => $gutter};

  /* 호출자가 className으로 넘기는 레이아웃 클래스(예: .body { overflow: hidden })보다
     overflow 선언이 우선하도록 c6 스타일 조각을 한 단계 특이도 올린다. */
  && {
    ${({ $orientation }) => ORIENTATION_STYLES[$orientation]}
  }
`;

export interface ScrollbarProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  /** 스크롤 축. 기본 `vertical`. */
  orientation?: ScrollbarOrientation;
  /**
   * 스크롤바 gutter 예약 정책. 기본 `stable`(스크롤바 등장 시 콘텐츠 폭 흔들림 방지). 테두리 프레임 안에서
   * 빈 gutter 띠가 보이는 표면은 `auto`로 둔다(실제 스크롤바가 있을 때만 gutter 확보).
   */
  gutter?: ScrollbarGutter;
  /** polymorphic element. 기본 `div`. */
  as?: ElementType;
  /**
   * 스크롤 컨테이너 DOM 접근용 ref — IntersectionObserver root, scroll 위치 복원·측정,
   * programmatic scroll 등에 쓴다. 기본 element는 `div`이며, `as`로 element를 바꾸면
   * 호출자가 ref 타입을 맞춘다.
   */
  ref?: Ref<HTMLDivElement>;
}

/**
 * 스크롤 컨테이너 경계 컴포넌트.
 *
 * c6 `foundations/scrollbar`의 thin themed thumb 스타일 위에, 스크롤 컨테이너가
 * 올바로 동작하기 위한 정책(min-size 0, 넘치는 축의 overscroll 격리, gutter 안정화)을 한곳에 묶고
 * 스크롤 컨테이너 DOM을 ref로 노출한다.
 *
 * 이미 styled 처리한 자기 엘리먼트에 thumb 모양만 입히면 되는 경우엔, 이 컴포넌트
 * 대신 `yScrollbarStyles`·`xScrollbarStyles`·`xyScrollbarStyles` 조각을 직접 합성한다.
 */
export function Scrollbar({
  children,
  orientation = "vertical",
  gutter = "stable",
  as,
  ref,
  ...rest
}: ScrollbarProps) {
  return (
    <StyledScrollbar as={as} $orientation={orientation} $gutter={gutter} ref={ref} {...rest}>
      {children}
    </StyledScrollbar>
  );
}

Scrollbar.displayName = "Scrollbar";
